import React, { useRef } from 'react';
import styled, { css } from 'styled-components';

import { Card } from './card';
import { basic } from '../../theme/colors';
import { mediaQuery } from '../../enums/mediaQuery';

export const Box = styled(Card)<{ gridColumn?: string; gridRow?: string; minHeight?: string; margin?: string }>`
  display: flex;
  flex: 1 1 25%;
  flex-direction: column;
  grid-column: ${(props) => props.gridColumn || 'initial'};
  grid-row: ${(props) => props.gridRow || 'initial'};

  ${(props) =>
    props.margin &&
    css`
      margin: ${props.margin};
    `}
  min-height: ${(props) => props.minHeight || 'initial'};
`;

export const BoxLabel = styled.div<{ align?: string; padding?: string; fontWeight?: string }>`
  display: block;
  text-align: ${(props) => props.align || 'center'};
  padding: ${(props) => props.padding || '0'};
  width: 100%;
  font-weight: ${(props) => props.fontWeight || 'initial'};
  @media ${mediaQuery.NOTEBOOK} {
    font-size: 14px;
  }
  @media ${mediaQuery.TABLET} {
    font-size: 12px;
  }
  @media ${mediaQuery.MOBILE} {
    font-size: 11px;
  }
`;

export const BoxCount = styled.div<{ noMargin?: boolean }>`
  display: block;
  width: 100%;
  margin-top: 1rem;
  margin-bottom: 1rem;
  text-align: center;
  font-weight: 800;
  font-size: 3rem;
  color: ${basic.haydenAlmostBlack};

  ${(props) =>
    props.noMargin &&
    css`
      margin-top: 0;
      margin-bottom: 0;
    `}

  .spinner-border {
    font-size: 1rem;
  }
`;

export const BoxSummaryCount = styled(BoxCount)`
  font-size: 2rem;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
`;

export const BoxVersion = styled(BoxCount)`
  font-size: 1.5rem;
`;

export const BoxTitle = styled.div`
  display: block;
  margin-top: 0;
  margin-bottom: 0;
  text-align: center;
  font-weight: 800;
  font-size: 1.5rem;
  color: ${basic.haydenAlmostBlack};
`;

export const BoxSubtitle = styled(BoxTitle)`
  font-size: 1.2rem;
`;

export const DateBox = styled(BoxCount)`
  font-size: 1.5rem;
  margin-top: 0.8rem;
  margin-bottom: 0;
`;

const BoxWithSlides = styled(Card)<{ gridColumn?: string }>`
  overflow: hidden;
  position: relative;
  top: 0;
  padding: 0;
  grid-column: ${(props) => props.gridColumn || 'initial'};
`;

const BoxArrow = styled.div<{ disabled: boolean }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  font-size: 2rem;
  cursor: pointer;
  padding: 0.5rem;
  transition: 0.3s;

  ${(props) =>
    props.disabled &&
    css`
      opacity: 0.5;
      cursor: auto;
    `}
`;

const BoxSlidesContainer = styled.div`
  overflow: hidden;
  position: relative;
  top: 0;
  width: 100%;
  height: 100%;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
  min-height: 152px;
`;

const BoxSlide = styled.div<{ index: number }>`
  width: 100%;
  height: 100%;
  padding: 1em 2em;
  position: absolute;
  top: 0;
  left: ${(props) => props.index * 100}%;
  scroll-snap-align: start;
  min-height: 152px;

  ${(props) =>
    props.index === 0 &&
    css`
      position: relative;
    `}
`;

const BoxRightArrow = (props: any) => {
  return (
    <BoxArrow style={{ right: '0' }} aria-label={props.disabled ? undefined : 'Next'} role="button" {...props}>
      ›
    </BoxArrow>
  );
};

const BoxLeftArrow = (props: any) => {
  return (
    <BoxArrow style={{ left: '0' }} aria-label={props.disabled ? undefined : 'Previous'} role="button" {...props}>
      ‹
    </BoxArrow>
  );
};

interface Props {
  children: React.ReactNode[];
  gridColumn?: string;
  mainLabel?: string;
}

export const BoxSlider = (props: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const { children } = props;

  const goLeft = () => {
    if (ref.current) {
      ref.current.scrollLeft -= ref.current.offsetWidth;

      // wrap around
      if (ref.current.scrollLeft < 0) {
        ref.current.scrollLeft = ref.current.scrollWidth - ref.current.clientWidth;
      }
    }
  };

  const goRight = () => {
    if (ref.current) {
      ref.current.scrollLeft += ref.current.offsetWidth;

      // wrap around
      if (ref.current.scrollLeft > ref.current.scrollWidth - ref.current.clientWidth) {
        ref.current.scrollLeft = 0;
      }
    }
  };

  return (
    <BoxWithSlides gridColumn={props.gridColumn}>
      {props.mainLabel && (
        <BoxLabel align="left" padding="1em 2em" fontWeight="bold">
          {props.mainLabel}
        </BoxLabel>
      )}

      {children.length > 1 && (
        <>
          <BoxLeftArrow onClick={goLeft} disabled={ref.current?.scrollLeft < 3} role="button" tabIndex={0} />
          <BoxRightArrow
            onClick={goRight}
            disabled={ref.current?.scrollLeft > ref.current?.scrollWidth - ref.current?.clientWidth * 1.51}
            role="button"
            tabIndex={0}
          />
        </>
      )}

      <BoxSlidesContainer ref={ref}>
        {children.map((child, index) => (
          <BoxSlide index={index} key={index}>
            {child}
          </BoxSlide>
        ))}
      </BoxSlidesContainer>
    </BoxWithSlides>
  );
};
